import { useApolloClient } from "@apollo/client";
import React, { useState, useEffect } from "react";
import { Alerts as AlertPopup } from "@aph/ui-lib-humanx";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import currentPatientIdSelector from "@/redux/slice/auth/selectors/currentPatientId";
import currentAddressIdSelector from "@/redux/slice/location/selectors/currentAddressId";
import isAddressListUpdatedSelector from "@/redux/slice/location/selectors/isAddressListUpdated";
import isAddressUpdatedSelector from "@/redux/slice/location/selectors/isAddressUpdated";
import latitudeSelector from "@/redux/slice/location/selectors/latitude";
import longitudeSelector from "@/redux/slice/location/selectors/longitude";
import pincodeSelector from "@/redux/slice/location/selectors/pincode";
import pharmaAddressDetailsSelector from "@/redux/slice/location/selectors/pharmaAddressDetails";
import placeDetailsStatusSelector from "@/redux/slice/location/selectors/placeDetailsStatus";
import { selectAllAddresses } from "@/redux/slice/location/selectors/addressDetails";
import signedInStatusSelector from "@/redux/slice/auth/selectors/signedInState";
import {
  updateAddressLoading,
  updateAlertMessage,
  updateCurrentAddressId,
  updateIsAddressListUpdated,
  updateIsAddressUpdated,
  updateIsAlertOpen,
  updatePlaceDetailsStatus,
  updateSpecifiedLocation,
} from "@/redux/slice/location/slice";
import { SignedinState } from "@/redux/slice/auth/enums/SignedInState";
import { ThunkResolvedStatus } from "@/libs/enum/ThunkResolvedStatus";
import { PatientAddress } from "@/redux/slice/location/types/PatientAddress";
import fetchPatientAddressList from "@/redux/slice/location/thunks/fetchPatientAddressList";
import locateCurrentLocation from "@/redux/slice/location/actions/locateCurrentLocation";
import { CurrentLocationUpdatedStatus } from "@/redux/slice/location/enums/CurrentLocationUpdatedStatus";
import currentLocationStatusSelector from "@/redux/slice/location/selectors/currentLocationStatus";
import isServiceable from "@/redux/slice/location/utils/isPincodeServiceable";
import authTokenSelector from "@/redux/slice/auth/selectors/authToken";
import isAlertOpenSelector from "@/redux/slice/location/selectors/isAlertOpen";
import alertMessageSelector from "@/redux/slice/location/selectors/alertMessage";
import Alerts from "@/components/Alerts";
import { setCartLocation, setUserActionPayload } from "@/redux/slice/cart/slice";
import styles from "./LocationManager.module.scss";

const LocationManager = (props) => {
  const dispatch = useAppDispatch();
  const apolloClient = useApolloClient();

  const [currentLocationUpdated, setCurrentLocationUpdated] = useState<boolean>(true);
  const [toastModal, setToastModal] = useState<string | null>(null);

  const pharmaAddressDetails = useAppSelector(pharmaAddressDetailsSelector);
  const patientId = useAppSelector(currentPatientIdSelector);
  const isAddressListUpdated = useAppSelector(isAddressListUpdatedSelector);
  const isAddressUpdated = useAppSelector(isAddressUpdatedSelector);
  const deliveryAddresses = useAppSelector(selectAllAddresses);
  const deliveryAddressId = useAppSelector(currentAddressIdSelector);
  const pincode = useAppSelector(pincodeSelector);
  const latitude = useAppSelector(latitudeSelector);
  const longitude = useAppSelector(longitudeSelector);
  const placeDetailsStatus = useAppSelector(placeDetailsStatusSelector);
  const signedInStatus = useAppSelector(signedInStatusSelector);
  const currentLocationStatus = useAppSelector(currentLocationStatusSelector);
  const authToken = useAppSelector(authTokenSelector);
  const alertMessage = useAppSelector(alertMessageSelector);
  const isAlertOpen = useAppSelector(isAlertOpenSelector);

  useEffect(() => {
    const handleAddressUpdate = () => {
      const updateAddress = (addresses: PatientAddress[], deliveryAddressId: string) => {
        const currentAddress = addresses?.find(({ id }) => id === deliveryAddressId) || null;

        if (!currentAddress) {
          dispatch(updateCurrentAddressId(""));
        } else {
          const { zipcode: pincode = "", city = "", state = "", latitude, longitude } = currentAddress;
          localStorage.setItem("pharmaPincode", pincode);
          dispatch(
            updateSpecifiedLocation({
              city,
              country: "",
              latitude,
              longitude,
              pincode,
              state,
            })
          );
          dispatch(updateCurrentAddressId(currentAddress.id));
        }

        dispatch(updateIsAddressUpdated(true));
      };

      if (!patientId) return;

      try {
        dispatch(updateAddressLoading(true));

        if (isAddressListUpdated) {
          updateAddress(deliveryAddresses, deliveryAddressId);
        }
      } catch (addressUpdateError) {
        dispatch(updateCurrentAddressId(""));
        console.error("UPDATING PATIENT ADDRESS FAILED", addressUpdateError);
      } finally {
        dispatch(updateAddressLoading(false));
      }
    };

    if (!isAddressUpdated && isAddressListUpdated) {
      handleAddressUpdate();
    }
  }, [isAddressUpdated, isAddressListUpdated, patientId, deliveryAddressId, deliveryAddresses]);

  useEffect(() => {
    const handleAddressListUpdate = () => {
      dispatch(fetchPatientAddressList({ apolloClient, patientId }));
    };

    if (!isAddressListUpdated && patientId) handleAddressListUpdate();
  }, [isAddressListUpdated, patientId]);

  useEffect(() => {
    if (placeDetailsStatus === ThunkResolvedStatus.fulfilled) {
      if (patientId) {
        dispatch(
          setUserActionPayload({
            address: {
              zipcode: pincode,
              coordinate: {
                latitude,
                longitude,
              },
            },
          })
        );
      } else {
        dispatch(updateAddressLoading(false));
      }

      dispatch(updatePlaceDetailsStatus(ThunkResolvedStatus.idle));
    }
  }, [patientId, pincode, latitude, longitude, placeDetailsStatus]);

  useEffect(() => {
    if (pharmaAddressDetails?.pincode && signedInStatus === SignedinState.SIGNEDOUT)
      localStorage.setItem("pharmaAddressDetails", JSON.stringify(pharmaAddressDetails));
  }, [pharmaAddressDetails, signedInStatus]);

  // why this hook here?
  useEffect(() => {
    dispatch(updateIsAddressListUpdated(false));
  }, []);

  useEffect(() => {
    if (signedInStatus === SignedinState.SIGNEDOUT) {
      const cartLocation = {
        pincode: pharmaAddressDetails?.pincode,
        lat: Number(pharmaAddressDetails?.lat),
        lng: Number(pharmaAddressDetails?.lng),
      };
      cartLocation?.pincode === "" ? dispatch(setCartLocation(null)) : dispatch(setCartLocation(cartLocation));
    }
  }, [pharmaAddressDetails?.pincode, signedInStatus]);

  useEffect(() => {
    if (!pharmaAddressDetails?.pincode && signedInStatus === SignedinState.SIGNEDOUT) {
      const pharmaAddressObj = localStorage.getItem("pharmaAddressDetails");
      const pharmaAddressDetails = pharmaAddressObj ? JSON.parse(pharmaAddressObj) : null;

      if (!pharmaAddressDetails?.pincode) {
        locateCurrentLocation(dispatch)
          .then((success) => {
            setCurrentLocationUpdated(false);
          })
          .catch((fail) => {
            console.log({ fail });
            setToastModal("Unable to detect current location");
          });
      }
    }
  }, [signedInStatus]);

  useEffect(() => {
    if (!currentLocationUpdated) {
      if (currentLocationStatus === CurrentLocationUpdatedStatus.fail) {
        console.error("Geolocation API could not fetch current location");
        setToastModal("Unable to detect current location");
        setCurrentLocationUpdated(true);
        dispatch(updateAddressLoading(false));
      } else if (currentLocationStatus === CurrentLocationUpdatedStatus.success) {
        setToastModal("Location detected and updated");
        setCurrentLocationUpdated(true);
        isServiceable(
          {
            pincode,
            latitude,
            longitude,
            authToken,
          },
          dispatch
        );
      }
    }
  }, [currentLocationStatus, currentLocationUpdated]);

  return (
    <>
      {isAlertOpen && (
        <Alerts
          setAlertMessage={(e) => {
            dispatch(updateAlertMessage(e));
          }}
          alertMessage={alertMessage}
          isAlertOpen={isAlertOpen}
          setIsAlertOpen={(e) => dispatch(updateIsAlertOpen(e))}
        />
      )}

      {toastModal && (
        <div className={styles.toastMessage}>
          <AlertPopup
            placement="top"
            open={toastModal ? true : false}
            severity="success"
            message={toastModal}
            onClick={() => {
              setToastModal(null);
            }}
            autoHide={10000}
          />
        </div>
      )}
    </>
  );
};

export default LocationManager;
